import React, { useState } from 'react';
import { useAdminConfig } from '../../hooks/useAdminConfig';
import { Card } from '../../components/ui/Card';
import { MasterDataTable } from '../../components/admin/MasterDataTable';
import { MasterDataForm } from '../../components/admin/MasterDataForm';
import { LoadingSpinner } from '../../components/ui/Feedback';
import { Department } from '../../types/masterData';

export const DepartmentsPage: React.FC = () => {
  const { departments, isLoading, createDepartment, updateDepartment, deleteDepartment } = useAdminConfig();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Department | null>(null);

  if (isLoading) return <LoadingSpinner size="lg" text="Loading Departments..." />;

  const fields = [
    { key: 'name', label: 'Department Name', required: true },
    { key: 'code', label: 'Department Code', required: true },
  ];

  const handleSave = async (data: Record<string, string>) => {
    if (editing) await updateDepartment(editing.department_id, data);
    else await createDepartment(data);
    setFormOpen(false);
  };

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-lg font-semibold text-slate-100">Departments</h3>
        <p className="text-xs text-slate-400">Manage organizational departments</p>
      </div>
      <Card>
        <MasterDataTable<Department & Record<string, unknown>>
          data={departments.map(d => ({ ...d }))}
          columns={[
            { key: 'name', label: 'Department Name', render: item => <span className="font-semibold text-slate-100">{item.name}</span> },
            { key: 'code', label: 'Code', render: item => <span className="text-xs bg-sky-500/10 text-sky-400 px-2 py-0.5 rounded font-mono">{item.code}</span> },
            { key: 'department_id', label: 'ID', render: item => <span className="text-[10px] font-mono text-slate-500">{item.department_id}</span> },
          ]}
          onAdd={() => { setEditing(null); setFormOpen(true); }}
          onEdit={item => { setEditing(item as unknown as Department); setFormOpen(true); }}
          onDelete={item => deleteDepartment((item as unknown as Department).department_id)}
          searchKey="name" searchPlaceholder="Search departments..."
        />
      </Card>
      <MasterDataForm isOpen={formOpen} onClose={() => setFormOpen(false)} onSave={handleSave}
        title={editing ? 'Edit Department' : 'Add New Department'} fields={fields}
        initialData={editing ? { name: editing.name, code: editing.code } : undefined} />
    </div>
  );
};
